import { MouseEvent, useContext } from 'react';
import { CartContext } from 'context-providers/cart-provider';
import { ProductsContext } from 'context-providers/products-provider';
import styled from 'styled-components';
import { PriceDetailsContainer } from 'components/library/ProductCard/product-card.styles';
import { trackButtonEvent } from 'lib/analytics';
import { formatMoney } from 'lib/utils/format-money';
import { applyShopifyPercentDiscount } from 'lib/utils/shopify-percent-discount';
import { BUNDLE_DISCOUNT_PERCENT } from 'lib/constants/bundle-discount';
import { BodyMedBold, BodySmReg } from '@seed-health/tokens';
import MutableNextLink from 'components/reusable/mutable-next-line';

const UpsellAddToCart = styled(MutableNextLink)`
  appearance: none;
  margin-left: auto;
  border: 1px solid var(--outline-primary);
  border-radius: 50px;
  padding: 11px 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 32px;
  color: var(--text-primary);
  gap: var(--spacing--base, 8px);
  background: transparent;
  cursor: pointer;
`;

const Upsell = styled.div`
  margin-top: 1.5rem;
  border-radius: 1rem;
  padding: 0.5rem;
  display: flex;
  gap: 0.75rem;
  align-items: center;
  background-color: var(--fill-secondary);
  overflow: hidden;
`;

const UpsellTitle = styled.p`
  margin-bottom: var(--spacing-4);
  ${BodyMedBold};
`;

const UpsellImage = styled.img`
  display: block;
  width: 7rem;
  height: 7rem;
  object-fit: cover;
`;

const UpsellPriceWrapper = styled.div`
  display: flex;
  align-items: center;

  .sale-price {
    color: var(--text-secondary);
    text-decoration: line-through;
  }
`;

const UpsellContent = styled.div`
  ${BodySmReg};
  margin-bottom: 0.25rem;
`;

const UpsellItem = ({ product }) => {
  const { userCart, openCart } = useContext(CartContext);
  const { getProduct, currencyCode } = useContext(ProductsContext);
  const { cartContains } = userCart;

  const upsellMatch = userCart.upsellItems.filter(
    (item) => item.code !== product.code,
  )[0];

  const upsellItem = upsellMatch ? getProduct(upsellMatch.productSku) : null;

  if (!upsellItem?.shopifyData || cartContains(upsellItem.productSku)) {
    return null;
  }

  const productPrice = parseFloat(product.shopifyData.price.amount);
  const upsellPrice = parseFloat(upsellItem.shopifyData.price.amount);

  const totalPrice = productPrice + upsellPrice;
  // Shopify discounts each line separately, so sum the discounted lines —
  // taking 75% of the combined total can come out a cent below checkout.
  const discountedPrice =
    Number(applyShopifyPercentDiscount(productPrice, BUNDLE_DISCOUNT_PERCENT)) +
    Number(applyShopifyPercentDiscount(upsellPrice, BUNDLE_DISCOUNT_PERCENT));

  const handleButtonClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    trackButtonEvent('Cross Sell', window.location.pathname, {});
    openCart({ skus: [product.productSku, upsellItem.productSku] });
  };

  return (
    <Upsell>
      <UpsellImage
        src={upsellItem.shopifyData.featuredImage.url}
        alt={upsellItem.shopifyData.featuredImage.altText}
        loading='lazy'
      />
      <div>
        <UpsellTitle>Bundle + Save 25%</UpsellTitle>
        <UpsellContent>
          Add {upsellItem.name.replace('™', '®')} to your routine and save on
          your first order.
        </UpsellContent>
        <div>
          <UpsellPriceWrapper>
            <PriceDetailsContainer>
              <span>{formatMoney(discountedPrice, currencyCode)}</span>
              <span className='sale-price'>
                {formatMoney(totalPrice, currencyCode)}
              </span>
            </PriceDetailsContainer>
            <UpsellAddToCart
              href={`/cart?cart=${product.productSku},${upsellItem.productSku}`}
              onClick={handleButtonClick}
            >
              Add
            </UpsellAddToCart>
          </UpsellPriceWrapper>
        </div>
      </div>
    </Upsell>
  );
};

export default UpsellItem;
